import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Bitget SPOT WS:
 * - WS: wss://ws.bitget.com/v2/ws/public
 * - ticker: {instType: "SPOT", channel: "ticker", instId: "ETHUSDT"}
 * - orderbook: {instType: "SPOT", channel: "books", instId: "ETHUSDT"}
 *   books: 1-й push = snapshot, дальше action: "update" — инкременты
 */
export const useBitgetSpot = (symbol) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const [tickerData, setTickerData] = useState(null); // {lastPrice, volume, volume24, bid, ask}
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });

  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const animationFrameRef = useRef(null);

  const orderBookRef = useRef({ asks: [], bids: [] });
  const hasSnapshotRef = useRef(false);
  const lastSeqRef = useRef(null);

  const log = (...a) => console.log("🔷 [BITGET SPOT]", ...a);

  const toPairs = (levels) => {
    if (!Array.isArray(levels)) return [];
    return levels
      .map(([p, q]) => [String(p), String(q)])
      .filter(([, q]) => parseFloat(q) >= 0);
  };

  const applyDeltas = useCallback((levels, side) => {
    const base = [...orderBookRef.current[side]];

    levels.forEach(([price, qty]) => {
      const pn = parseFloat(price);
      const qn = parseFloat(qty);
      const idx = base.findIndex(([p]) => parseFloat(p) === pn);

      if (qn === 0) {
        if (idx !== -1) base.splice(idx, 1);
      } else {
        if (idx !== -1) {
          base[idx] = [price, qty];
        } else {
          base.push([price, qty]);
        }
      }
    });

    orderBookRef.current[side] =
      side === "asks"
        ? base.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).slice(0, 20)
        : base.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])).slice(0, 20);

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      setOrderBook({ ...orderBookRef.current });
    });
  }, []);

  const subscribeAll = useCallback((ws, instId) => {
    const args = [
      { instType: "SPOT", channel: "ticker", instId },
      { instType: "SPOT", channel: "books",  instId },
    ];
    const sub = { op: "subscribe", args };
    ws.send(JSON.stringify(sub));
    log("→ SUB", sub);
  }, []);

  const unsubscribeAll = useCallback((ws, instId) => {
    try {
      const args = [
        { instType: "SPOT", channel: "ticker", instId },
        { instType: "SPOT", channel: "books",  instId },
      ];
      ws?.send(JSON.stringify({ op: "unsubscribe", args }));
      log("→ UNSUB", args);
    } catch {}
  }, []);

  const handleMessage = useCallback((event) => {
    try {
      if (typeof event.data === "string") {
        // сервер отвечает "pong" на наш "ping"
        if (event.data === "pong") return;

        // подтверждения подписок
        try {
          const msg = JSON.parse(event.data);
          if (msg?.event) {
            log("evt:", msg.event, msg.arg || msg.args || "");
            return;
          }
          if (!msg?.data || !msg?.arg) return;

          const chan = msg.arg.channel;
          const action = msg.action; // "snapshot" | "update"
          const payload = Array.isArray(msg.data) ? msg.data[0] : msg.data;

          // --- TICKER ---
          if (chan === "ticker") {
            const lastPr = payload?.lastPr;
            const bidPr  = payload?.bidPr;
            const askPr  = payload?.askPr;
            const baseV  = payload?.baseVolume;
            const quoteV = payload?.quoteVolume;

            setTickerData((prev) => ({
              ...(prev || {}),
              symbol: msg.arg?.instId,
              lastPrice: lastPr != null ? String(lastPr) : prev?.lastPrice,
              // Для UI — quote объём (в USDT). Дублируем в volume/volume24
              volume: quoteV != null ? String(quoteV) : (prev?.volume ?? ""),
              volume24: quoteV != null ? String(quoteV) : (prev?.volume24 ?? ""),
              bid: bidPr,
              ask: askPr,
            }));
            return;
          }

          // --- BOOKS (snapshot + update) ---
          if (chan === "books") {
            const seq = payload?.seq ?? null;
            const asks = toPairs(payload?.asks || []);
            const bids = toPairs(payload?.bids || []);

            if (action === "snapshot" || !hasSnapshotRef.current) {
              orderBookRef.current = {
                asks: asks.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).slice(0, 20),
                bids: bids.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])).slice(0, 20),
              };
              setOrderBook({ ...orderBookRef.current });
              hasSnapshotRef.current = true;
              lastSeqRef.current = seq;
              log("📊 snapshot set. seq=", seq, "asks/bids:", asks.length, bids.length);
              return;
            }

            // updates — пропускаем старые по seq, если есть
            if (seq != null && lastSeqRef.current != null && Number(seq) <= Number(lastSeqRef.current)) {
              log("⏭ stale update skipped seq=", seq, "local=", lastSeqRef.current);
              return;
            }
            lastSeqRef.current = seq;

            if (asks.length) applyDeltas(asks, "asks");
            if (bids.length) applyDeltas(bids, "bids");
            return;
          }
        } catch {
          // не JSON — игнор (например, "pong")
        }
      }
    } catch (e) {
      log("❌ parse error:", e);
    }
  }, [applyDeltas]);

  useEffect(() => {
    if (!symbol) return;

    const instId = symbol.includes("USDT") ? symbol : `${symbol}USDT`;

    setIsConnected(false);
    setError(null);
    setTickerData(null);
    setOrderBook({ asks: [], bids: [] });

    orderBookRef.current = { asks: [], bids: [] };
    hasSnapshotRef.current = false;
    lastSeqRef.current = null;

    const ws = new WebSocket("wss://ws.bitget.com/v2/ws/public");
    wsRef.current = ws;

    ws.onopen = () => {
      log("🔗 open:", instId);
      setIsConnected(true);
      subscribeAll(ws, instId);

      // heartbeat: отправляем "ping" раз в 25–30s
      pingTimerRef.current = setInterval(() => {
        try { ws.send("ping"); } catch {}
      }, 30000);
    };

    ws.onmessage = handleMessage;

    ws.onerror = (e) => {
      log("❌ ws error:", e);
      setError(e?.message || "WS error");
      setIsConnected(false);
    };

    ws.onclose = () => {
      log("🔌 close");
      setIsConnected(false);
    };

    return () => {
      try {
        unsubscribeAll(ws, instId);
        ws.close();
      } catch {}
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
      wsRef.current = null;
    };
  }, [symbol, subscribeAll, unsubscribeAll, handleMessage]);

  return { isConnected, error, orderBook, tickerData };
};
